'use client';
import React from 'react';
import Icon from '@/components/ui/AppIcon';
import AppLogo from '@/components/ui/AppLogo';
import { SITE_NAME } from '@/lib/site-config';

interface AppDownloadCTAProps {
  onDownloadClick: () => void;
}

export default function AppDownloadCTA({ onDownloadClick }: AppDownloadCTAProps) {
  return (
    <section className="section-py bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="relative overflow-hidden rounded-3xl bg-foreground p-8 md:p-14 text-center">
          {/* Subtle dot grid */}
          <div className="absolute inset-0 opacity-5 pointer-events-none dot-grid-bg" />

          {/* Color accent blobs */}
          <div
            className="absolute top-0 left-0 w-64 h-64 rounded-full pointer-events-none opacity-20"
            style={{ background: 'radial-gradient(circle, #FF5722 0%, transparent 70%)', filter: 'blur(50px)', transform: 'translate(-30%, -30%)' }}
          />
          <div
            className="absolute bottom-0 right-0 w-64 h-64 rounded-full pointer-events-none opacity-15"
            style={{ background: 'radial-gradient(circle, #1A237E 0%, transparent 70%)', filter: 'blur(50px)', transform: 'translate(30%, 30%)' }}
          />

          {/* Floating icons */}
          <div className="absolute top-8 left-8 opacity-10" style={{ animation: 'float 5s ease-in-out infinite' }}>
            <Icon name="StarIcon" size={40} variant="solid" className="text-white" />
          </div>
          <div className="absolute bottom-8 right-8 opacity-10" style={{ animation: 'float 7s ease-in-out infinite', animationDelay: '2s' }}>
            <Icon name="CurrencyRupeeIcon" size={48} className="text-white" />
          </div>

          <div className="relative z-10 max-w-2xl mx-auto">
            <div className="flex justify-center mb-5">
              <AppLogo size={48} />
            </div>
            <h2 className="font-heading font-bold text-white mb-4" style={{ fontSize: 'clamp(1.75rem, 4vw, 2.75rem)' }}>
              Download {SITE_NAME} App
            </h2>
            <p className="text-white/60 mb-8 leading-relaxed">
              Apply to campaigns, submit reviews, track earnings, and get paid — all in one app. Available on Android & iOS.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={onDownloadClick}
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl px-6 py-3.5 transition-colors group"
              >
                <Icon name="DevicePhoneMobileIcon" size={22} className="text-white/70 group-hover:text-white transition-colors" />
                <div className="text-left">
                  <div className="text-[9px] text-white/40 uppercase tracking-wide">Get it on</div>
                  <div className="text-sm font-bold font-heading text-white">Google Play Store</div>
                </div>
              </button>
              <button
                onClick={onDownloadClick}
                className="flex items-center gap-3 bg-white/10 hover:bg-white/15 border border-white/15 rounded-xl px-6 py-3.5 transition-colors group"
              >
                <Icon name="DevicePhoneMobileIcon" size={22} className="text-white/70 group-hover:text-white transition-colors" />
                <div className="text-left">
                  <div className="text-[9px] text-white/40 uppercase tracking-wide">Download on the</div>
                  <div className="text-sm font-bold font-heading text-white">Apple App Store</div>
                </div>
              </button>
            </div>

            {/* Stats */}
            <div className="flex flex-wrap justify-center gap-8 mt-10 pt-8 border-t border-white/10">
              {[
                { value: '4.8/5', label: 'App Rating' },
                { value: '50K+', label: 'Downloads' },
                { value: '24/7', label: 'Support' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="font-heading font-bold text-white text-xl">{s.value}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/35">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}